

import { BCMSCurrency, DisabledPrompt, useRoute, useRouteData, useStateManager } from "@bcm-dev/bcms-vite";

import CustomSelect, { CustomSelectItem } from "components/custom-ui/CustomSelect";
import { FormContainer, FormRowView } from "components/shared";
import { FormButtonRow } from "components/shared/containers/FormContainer";
import InfoAlertView from "components/shared/views/InfoAlertView";
import { PrimaryButton, SecondaryButton } from "components/ui";
import { SVGAsset } from "res";
import { PositivePayCheck } from "model";
import "./styles.css";
import "./mobile.css";

/**
 * @function TASPositivePayImportChecksFileImportForm
 * @param {Object} props
 * @return {React.Component}
 * @description The form for importing checks from a file.
 */
export default function TASPositivePayImportChecksFileImportForm(props) {
    const { data, ui, onChange } = useStateManager();
    const { route } = useRouteData();
    const navigate = useRoute();
    let newTemplate = route ? route.newTemplate : undefined;
    let showNewTemplateBanner = newTemplate ? true : false;
    const hasTemplates = data.templates.length > 0;

    if(ui.showNewTemplateBanner.isActive) {
        showNewTemplateBanner = true;
        newTemplate = data.defaultTemplate;
    }

    const templates = data.templates;
    const chosenFile = data.positivePayCheckFile ? data.positivePayCheckFile.description : `No file chosen`;
    const selectedTemplate = data.selectedTemplate ? data.selectedTemplate.id : "none";
    const selectedValidTemplate = selectedTemplate !== "none" && chosenFile !== "No file chosen";
    const importButtonClasses = selectedValidTemplate ? { className: "" } : { className: "disabled-button" };
    const newHistory = route ? route.history : undefined;
    const newChecks = newHistory ? newHistory._checks : undefined;
    const confirmationNumber = newHistory ? newHistory._confirmationNumber : undefined;
    const fileName = newHistory ? newHistory._file._description : undefined;

    const newChecksAmount = newChecks ? newChecks.reduce((acc, item) => {
        const check = PositivePayCheck.initByID(item);
        let amount = check.amount instanceof BCMSCurrency ? check.amount.amount : check.amount;
        acc = check ? acc + parseFloat(amount) : acc;
        return acc;
    }, 0) : 0;
    const showSuccessHistoryBanner = ui.showSuccessHistoryBanner;
    const successHistory = data.defaultHistory;

    const defaultNewChecksAmount = successHistory.checks.reduce((acc, item) => {
        const check = PositivePayCheck.initByID(item);
        let amount = check.amount instanceof BCMSCurrency ? check.amount.amount : check.amount;
        acc = check ? acc + parseFloat(amount) : acc;
        return acc;
    }, 0);

    const defaultNewChecksAmountFormatted = BCMSCurrency.init(defaultNewChecksAmount).format();
    const newChecksAmountFormatted = BCMSCurrency.init(newChecksAmount).format();

    const selectItems = templates.reduce((acc, item, index) => {
        acc.push(
            <CustomSelectItem key={ item.id } value={ item.id }
            dataPoint={ `TAS_Positive_Pay_Import_Checks_File_Import_Form_Choose_Template_Option_${ item.id }` }>
                { item.name }
            </CustomSelectItem>
        );
        return acc;
    }, [<CustomSelectItem key="none" className="none" value="none">None</CustomSelectItem>]);

    if(route) {
        window.history.replaceState({}, document.title);
    }

    const onImportClick = evt => {
        navigate(
            "/tasPositivePayImportChecksProcessing", 
            { state: { templateID: selectedTemplate, positivePayCheckFileID: data.positivePayCheckFile.id }}
        );
    };

    return (
        <div className="tas-positive-pay-import-checks-form">
            <div className="flex-container flex-horizontal">
                { newChecks &&
                <InfoAlertView className="set-width checkmark-style" 
                text={ `You have successfully imported ${ newChecks.length } checks with a total amount of ${ newChecksAmountFormatted }
                from the ${ fileName } file. Your confirmation number is ${ confirmationNumber }.` }
                dataPoint={ `TAS_Positive_Pay_Import_Checks_File_Import_Form_Successfully_Imported_Checks_Banner` }
                icon={ <SVGAsset id="CircleCheckmarkIcon" className="checkmark-icon" /> }/> 
                }
                { showSuccessHistoryBanner.isActive && 
                <InfoAlertView className="set-width checkmark-style" 
                text={ `You have successfully imported ${ successHistory.checks.length } checks with a total amount of ${ defaultNewChecksAmountFormatted }
                from the ${ successHistory.file.description } file. Your confirmation number is ${ successHistory.confirmationNumber }.` }
                dataPoint={ `TAS_Positive_Pay_Import_Checks_File_Import_Form_Successfully_Imported_Checks_Banner` }
                icon={ <SVGAsset id="CircleCheckmarkIcon" className="checkmark-icon" /> }/> 
                }
                { showNewTemplateBanner && !newChecks &&
                <InfoAlertView className="set-width checkmark-style" text={ `You have successfully created the ${ newTemplate._name } template.` }
                    icon={ <SVGAsset id="CircleCheckmarkIcon" className="checkmark-icon" /> }/> 
                }
                { !showNewTemplateBanner && !newChecks && !showSuccessHistoryBanner.isActive && !hasTemplates &&
                <InfoAlertView className="set-width" text="You need to create a template before importing checks."
                dataPoint={ `TAS_Positive_Pay_Import_Checks_File_Import_Form_Create_Template_Banner` }
                icon={ <SVGAsset id="InfoIcon" /> }/>
                }
            </div>
            <FormContainer className="small-form-width">
                <FormRowView text="Choose Template">
                    <CustomSelect active={ ui.chooseTemplateSelect.isActive } value={ selectedTemplate }
                    dataPoint={ `TAS_Positive_Pay_Import_Checks_File_Import_Form_Choose_Template_Select` }
                    onChange={ onChange("selectedTemplate") }>
                        { selectItems }
                    </CustomSelect>
                </FormRowView>
                <FormRowView text="File">
                    <div className="flex-container flex-horizontal align-center choose-file-container">
                        <SecondaryButton text="Choose File"
                        dataPoint={ `TAS_Positive_Pay_Import_Checks_File_Import_Form_Choose_File_Button` }
                        onClick={ onChange("positivePayCheckFile") } />
                        <p className="set-width">{ chosenFile }</p>
                    </div>
                </FormRowView>
                <FormRowView>
                    <p>Maximum file size: 500 KB</p>
                    <p>Valid file extensions: .csv, .txt</p>
                </FormRowView>
                <FormButtonRow>
                    <DisabledPrompt>
                        <SecondaryButton text="Cancel" dataPoint={ `TAS_Positive_Pay_Import_Checks_File_Import_Form_Cancel_Button` }/>
                    </DisabledPrompt>
                    <PrimaryButton { ...importButtonClasses } text="Import"
                    dataPoint={ `TAS_Positive_Pay_Import_Checks_File_Import_Form_Import_Button` }
                    onClick={ onImportClick } />
                </FormButtonRow>
            </FormContainer>
        </div>
    );
}